import React, { memo } from 'react';
import { useKeybinding } from '../hooks/keybinding/useKeybinding.js';
import { toast } from 'sonner';
import { markdownToReadableText } from '../utils/markdownUtils.js';
import PropTypes from 'prop-types';

/**
 * ChatNavigationKeybindings - Component that registers chat navigation keybindings
 * 
 * This component must be inside the KeybindingProvider to access the context.
 * It registers Vim-style navigation keybindings for chat messages.
 */
const ChatNavigationKeybindings = ({ chatNavigation, scrollUp, scrollDown, messages = [] }) => {
  // Get chat navigation functions from props
  const {
    focusNextMessage,
    focusPreviousMessage,
    focusFirstMessage,
    focusLastMessage,
    clearFocus,
    isNavigationActive,
    focusedMessageIndex,
  } = chatNavigation;

  // Copy focused message content (plain text, stripped of markdown)
  const copyFocusedMessageContent = async () => {
    if (focusedMessageIndex >= 0 && focusedMessageIndex < messages.length) {
      const message = messages[focusedMessageIndex];
      try {
        const plainText = markdownToReadableText(message.content || '');
        await navigator.clipboard.writeText(plainText);
        toast('Message content copied to clipboard');
      } catch {
        toast('Failed to copy message content');
      }
    }
  };

  // Copy focused message markdown (raw markdown with formatting)
  const copyFocusedMessageMarkdown = async () => {
    if (focusedMessageIndex >= 0 && focusedMessageIndex < messages.length) {
      const message = messages[focusedMessageIndex];
      // Copy the raw markdown content with all formatting
      try {
        await navigator.clipboard.writeText(message.content || '');
        toast('Raw markdown copied to clipboard');
      } catch {
        toast('Failed to copy raw markdown');
      }
    }
  };

  // Helper function to check if we should allow keybindings
  const shouldAllowKeybinding = () => {
    const activeElement = document.activeElement;
    if (!activeElement) return true;
    
    // Allow keybindings if not in an input/textarea, OR if in command menu input
    const isInput = ['INPUT', 'TEXTAREA'].includes(activeElement.tagName);
    const isCommandMenuInput = activeElement.getAttribute('cmdk-input') !== null;
    
    return !isInput || isCommandMenuInput;
  };

  // Register chat navigation keybindings
  useKeybinding({
    'j': {
      handler: () => focusNextMessage && focusNextMessage(),
      description: 'Navigate to next message',
      disabledReason: 'Focus on main chat area first',
      when: () => shouldAllowKeybinding() && !!focusNextMessage
    },
    'k': {
      handler: () => focusPreviousMessage && focusPreviousMessage(),
      description: 'Navigate to previous message',
      disabledReason: 'Focus on main chat area first',
      when: () => shouldAllowKeybinding() && !!focusPreviousMessage
    },
    'g g': {
      handler: () => focusFirstMessage && focusFirstMessage(),
      description: 'Go to first message',
      disabledReason: 'Focus on main chat area first',
      when: () => shouldAllowKeybinding() && !!focusFirstMessage
    },
    'Shift+G': {
      handler: () => focusLastMessage && focusLastMessage(),
      description: 'Go to last message and scroll to bottom',
      disabledReason: 'Focus on main chat area first',
      when: () => shouldAllowKeybinding() && !!focusLastMessage
    },
    'Escape': {
      handler: () => {
        if (isNavigationActive && clearFocus) {
          clearFocus();
          return true; // Prevent default escape behavior when clearing focus
        }
        return false; // Allow normal escape behavior
      },
      description: 'Clear message focus',
      when: () => isNavigationActive && !!clearFocus
    },
    
    // Smooth scrolling
    'd': {
      handler: () => scrollDown && scrollDown(),
      description: 'Scroll down',
      disabledReason: 'Focus on main chat area first',
      when: () => shouldAllowKeybinding() && !!scrollDown
    },
    'u': {
      handler: () => scrollUp && scrollUp(),
      description: 'Scroll up',
      disabledReason: 'Focus on main chat area first',
      when: () => shouldAllowKeybinding() && !!scrollUp
    },

    // Copy operations
    'y y': {
      handler: () => copyFocusedMessageContent(),
      description: 'Copy focused message as plain text',
      disabledReason: 'No message focused - use j/k to focus a message first',
      when: () => shouldAllowKeybinding() && 
               isNavigationActive && 
               focusedMessageIndex >= 0 && 
               focusedMessageIndex < messages.length
    },
    'y m': {
      handler: () => copyFocusedMessageMarkdown(),
      description: 'Copy focused message as raw markdown',
      disabledReason: 'No message focused - use j/k to focus a message first',
      when: () => shouldAllowKeybinding() && 
               isNavigationActive && 
               focusedMessageIndex >= 0 && 
               focusedMessageIndex < messages.length
    },
  }, [focusNextMessage, focusPreviousMessage, focusFirstMessage, focusLastMessage, clearFocus, isNavigationActive, scrollUp, scrollDown, copyFocusedMessageContent, copyFocusedMessageMarkdown, focusedMessageIndex, messages.length]);

  // This component renders nothing - it just registers keybindings
  return null;
};

ChatNavigationKeybindings.propTypes = {
  chatNavigation: PropTypes.shape({
    focusNextMessage: PropTypes.func,
    focusPreviousMessage: PropTypes.func,
    focusFirstMessage: PropTypes.func,
    focusLastMessage: PropTypes.func,
    clearFocus: PropTypes.func,
    isNavigationActive: PropTypes.bool,
    focusedMessageIndex: PropTypes.number,
  }).isRequired,
  scrollUp: PropTypes.func.isRequired,
  scrollDown: PropTypes.func.isRequired,
  messages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    content: PropTypes.string,
    isUser: PropTypes.bool,
  })),
};

export default memo(ChatNavigationKeybindings);